import { DialogRef } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TenantKey } from '@app/core/enums/tenant-key.enum';
import { GuardianshipOverviewStateService } from '@app/guardianship/shared/services';
import { waitForNavigationEnd } from '@domains/auth/components/login-modal/operators/wait-for-login-navigation.operator';
import { ConfirmationModalData } from '@shared/components/confirmation-modal/confirmation-modal.model';
import { ToastService } from '@shared/components/toast';
import {
  CourtService,
  DashboardNavigationService,
  LoaderService,
} from '@shared/services';
import {
  extractErrorMessage,
  getPhoneNumberToPlainNumber,
} from '@shared/utils';
import { tap, finalize, catchError, of } from 'rxjs';
import { objectToSnake } from 'ts-case-convert';
import { CLERK_CREATE_APP_TOAST_CONFIG } from '../../shared/constants/clerk-create-app-toast.constants';
import { getConfirmationData } from '../../shared/helpers/get-confirmation-data.helper';
import { ClerkCreateGuardianshipPayload } from '../../shared/interfaces/clerk-create-guardianship-payload.interface';
import { ClerkCreateApplicationHttpService } from '../../shared/services/clerk-create-application-http.service';
import { RegisterGuardianshipFormService } from './register-guardianship-form.service';
import { FormErrorService } from '@shared/components';

@Injectable({
  providedIn: 'root',
})
export class ClerkCreateGuardianshipFacadeService {
  private readonly _formService = inject(RegisterGuardianshipFormService);
  private readonly _formErrorService = inject(FormErrorService);

  private readonly _toast = inject(ToastService);
  private readonly _router = inject(Router);
  private readonly _clerkCreateApplicationHttpService = inject(
    ClerkCreateApplicationHttpService,
  );
  private readonly _courtService = inject(CourtService);
  private readonly _guardianshipOverviewState = inject(
    GuardianshipOverviewStateService,
  );
  private readonly _dashboardNavigationService = inject(
    DashboardNavigationService,
  );
  private readonly _loaderService = inject(LoaderService);

  private readonly _dialogRef = inject(DialogRef);

  private readonly _confirmationData: ConfirmationModalData =
    getConfirmationData('G&C');

  private readonly _form = this._formService.form;

  public getConfirmationData(): ConfirmationModalData {
    return this._confirmationData;
  }

  public submitApplication() {
    if (this._form.invalid) return;

    const rawPayload = this._form.getRawValue();
    const payload = objectToSnake({
      email: rawPayload.email,
      isFirm: rawPayload.isFirm,
      firstName: rawPayload.firstName,
      middleName: rawPayload.middleName,
      lastName: rawPayload.lastName,
      username: rawPayload.username,
      userId: rawPayload.userId === 0 ? null : rawPayload.userId,
      phoneNumber: getPhoneNumberToPlainNumber(rawPayload.phoneNumber),
      prefixPhone: rawPayload.prefixPhone,
    });

    const payloadToSnake: ClerkCreateGuardianshipPayload = {
      ...payload,
      court_order: rawPayload.courtOrder!,
    };
    const formData = new FormData();

    for (const [key, value] of Object.entries(payloadToSnake)) {
      if (key === 'user_id' && value === null) {
        formData.append(key, '');
      } else if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    }

    this._formService.setIsLoading(true);
    this._loaderService.show();
    this._clerkCreateApplicationHttpService
      .createGuardianship(Number(this._courtService.courtId()), formData)
      .pipe(
        tap(({ guardianId }) => {
          this._guardianshipOverviewState.setGuardianshipId(Number(guardianId));
          this._dashboardNavigationService.goToDashboard(
            TenantKey.GUARDIANSHIP,
          );
        }),
        waitForNavigationEnd(this._router, this._toast),
        tap(() => {
          this._dialogRef.close();
        }),
        finalize(() => {
          this._formService.setIsLoading(false);
          this._loaderService.hide();
        }),
        catchError((error) => {
          const errorMessage = extractErrorMessage(error);

          this._formErrorService.displaySimpleErrorModal(
            errorMessage,
            'Error in Register',
          );

          this._toast.show(CLERK_CREATE_APP_TOAST_CONFIG['error']);
          return of(null);
        }),
      )
      .subscribe();
  }
}
